// frontend/src/pages/DailyDigestPage.js
import React, { useState, useEffect } from 'react';
import { getDailyDigest } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ThemeArticles from '../components/Dashboard/ThemeArticles';
import { 
  CalendarDaysIcon, 
  NewspaperIcon, 
  LightBulbIcon,
  ChevronLeftIcon,
  ChevronRightIcon 
} from '@heroicons/react/24/outline';

const DIGEST_DAYS = parseInt(process.env.REACT_APP_DIGEST_DAYS_TO_SHOW || '7', 10);

const getLocalDateString = (date) => {
  return date.toLocaleDateString('en-CA');
};

const DailyDigestPage = () => {
  const [selectedDate, setSelectedDate] = useState(getLocalDateString(new Date()));
  const [digest, setDigest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [availableDates, setAvailableDates] = useState([]);

  // Generate last 7 days worth of dates
  const generateDateOptions = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i < DIGEST_DAYS; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      dates.push(getLocalDateString(date));
    }
    return dates;
  };

  // Initialize available dates and load initial digest
  useEffect(() => {
    const dates = generateDateOptions();
    setAvailableDates(dates);
    
    // Try to load digest for today first, then fallback to previous days
    const loadInitialDigest = async () => {
      setIsLoading(true);
      setError('');
      
      for (const date of dates) {
        try {
          const data = await getDailyDigest(date);
          if (data && data.id) {
            setDigest(data);
            setSelectedDate(date);
            setIsLoading(false);
            return;
          }
        } catch (err) {
          // Continue to next date if this one fails
          console.log(`No digest found for ${date}, trying next date...`);
        }
      }
      
      // If no digest found for any of the 3 days
      setError('No digest available for the last 3 days.');
      setDigest(null);
      setIsLoading(false);
    };

    loadInitialDigest();
  }, []);

  // Load digest when date changes
  const loadDigest = async (date) => {
    setIsLoading(true);
    setError('');
    
    try {
      const data = await getDailyDigest(date);
      if (data && data.id) {
        setDigest(data);
        setSelectedDate(date);
      } else {
        setError('Daily digest not found for this date.');
        setDigest(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load daily digest.');
      setDigest(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display
  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString + "T00:00:00Z");
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    
    if (dateString === getLocalDateString(today)) {
      return 'Today';
    } else if (dateString === getLocalDateString(yesterday)) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      });
    }
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString + "T00:00:00Z");
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  // Navigate to previous/next available date
  const navigateDate = (direction) => {
    const currentIndex = availableDates.indexOf(selectedDate);
    let nextIndex;
    
    if (direction === 'prev') {
      nextIndex = currentIndex + 1;
    } else {
      nextIndex = currentIndex - 1;
    }
    
    if (nextIndex >= 0 && nextIndex < availableDates.length) {
      loadDigest(availableDates[nextIndex]);
    }
  };

  // Parse digest content
  let parsedContent = null;
  if (digest && typeof digest.content === 'string') {
    try {
      parsedContent = JSON.parse(digest.content);
    } catch (e) {
      console.error("[DailyDigestPage] Failed to parse digest content JSON string:", e);
    }
  } else if (digest && typeof digest.content === 'object') {
    parsedContent = digest.content;
  }

  const currentDateIndex = availableDates.indexOf(selectedDate);
  const canGoPrev = currentDateIndex < availableDates.length - 1;
  const canGoNext = currentDateIndex > 0;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header with Date Navigation */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200/50 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CalendarDaysIcon className="h-8 w-8 text-blue-100 mr-3" />
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Daily Digest</h1>
              </div>
              
              {/* Date Navigation */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigateDate('prev')}
                  disabled={!canGoPrev || isLoading}
                  className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                <div className="px-4 py-2 bg-white/20 rounded-lg">
                  <span className="text-white font-medium">
                    {selectedDate ? formatDisplayDate(selectedDate) : 'Loading...'}
                  </span>
                </div>
                
                <button
                  onClick={() => navigateDate('next')}
                  disabled={!canGoNext || isLoading}
                  className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Date Tabs */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex space-x-1">
              {availableDates.map((date) => (
                <button
                  key={date}
                  onClick={() => loadDigest(date)}
                  disabled={isLoading}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedDate === date
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  } disabled:opacity-50`}
                >
                  {formatDisplayDate(date)}
                </button>
              ))}
            </div>
          </div>
          {/* Digest content - only show when digest is loaded */}
          {digest && !isLoading && parsedContent && (
            <>
              <div className="p-8 space-y-10">
                {/* Overview Section */}
                {parsedContent.overall_introduction && (
                  <section className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-100">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                        <NewspaperIcon className="h-5 w-5 text-white" />
                      </div>
                      Overview
                    </h3>
                    <div className="prose prose-lg max-w-none">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-lg">
                        {parsedContent.overall_introduction}
                      </p>
                    </div>
                  </section>
                )}

                {/* Featured Themes */}
                {parsedContent.featured_themes && parsedContent.featured_themes.length > 0 && (
                  <section className="space-y-8">
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                        <LightBulbIcon className="h-5 w-5 text-white" />
                      </div>
                      Featured Themes
                    </h3>
                    
                    <div className="space-y-8">
                      {parsedContent.featured_themes.map((theme, index) => (
                        <div 
                          key={index} 
                          className="bg-white rounded-xl shadow-md border border-gray-200/60 overflow-hidden hover:shadow-lg transition-shadow"
                        >
                          <div className="p-8">
                            <div className="flex items-start mb-6">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 ${
                                index % 3 === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                index % 3 === 1 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                'bg-gradient-to-r from-purple-500 to-purple-600'
                              }`}>
                                <LightBulbIcon className="h-6 w-6 text-white" />
                              </div>
                              <div className="flex-1">
                                <h4 className="text-xl font-bold text-gray-900 mb-3">
                                  {theme.theme_title || "Untitled Theme"}
                                </h4>
                                <div className="prose max-w-none">
                                  <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                                    {theme.theme_description || "No description for this theme."}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {theme.article_ids && theme.article_ids.length > 0 ? (
                              <div className="border-t border-gray-100 pt-6">
                                <h5 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                  <NewspaperIcon className="h-5 w-5 text-gray-500 mr-2" />
                                  Related Articles
                                </h5>
                                <ThemeArticles 
                                  articleIds={theme.article_ids} 
                                  themeTitle={theme.theme_title} 
                                />
                              </div>
                            ) : (
                              <div className="border-t border-gray-100 pt-6">
                                <p className="text-gray-500 italic">
                                  No specific articles were highlighted for this theme in the digest.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
                
                {/* Footer */}
                <div className="border-t border-gray-200 pt-8 mt-12">
                  <p className="text-sm text-gray-500 text-center">
                    Digest generated for {selectedDate ? formatFullDate(selectedDate) : ''}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <LoadingSpinner />
            <p className="mt-4 text-gray-600">Loading digest...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
            <div className="flex flex-col items-center">
              <NewspaperIcon className="h-16 w-16 text-red-300 mb-4" />
              <h3 className="text-lg font-semibold text-red-800 mb-2">Digest Not Available</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!digest && !isLoading && !error && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200/50 p-12 text-center">
            <CalendarDaysIcon className="h-16 w-16 text-gray-300 mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Digest Available</h3>
            <p className="text-gray-500">
              No digest content is available for the selected date.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyDigestPage;
